/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import dayjs from 'dayjs';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import RRule, { Weekday } from 'rrule';
import { Subject } from 'rxjs';
import { delay, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-recurrence-rule',
  templateUrl: './recurrence-rule.component.html',
  styleUrls: ['./recurrence-rule.component.scss']
})
export class RecurrenceRuleComponent implements OnInit, OnDestroy, OnChanges {
  @Input() recurrenceForm: FormGroup;
  @Input() today: any;
  @Input() options: any;
  @Input() timezone?: string = this.utilityService.timeZone;

  @Output() recurrenceRuleChanged = new EventEmitter<any[]>();

  days: Array<any> = [
    {
      week: [
        { label: 'Monday', value: false, key: 'MO' },
        { label: 'Tuesday', value: false, key: 'TU' },
        { label: 'Wednesday', value: false, key: 'WE' },
        { label: 'Thursday', value: false, key: 'TH' },
        { label: 'Friday', value: false, key: 'FR' },
        { label: 'Saturday', value: false, key: 'SA' },
        { label: 'Sunday', value: false, key: 'SU' },
      ]
    }
  ];

  recurrenceFormErrorMessages: { [key: string]: string } = {};
  rruleOptions: any;
  rruleText: string;
  totalInstance: number = 0;
  minEndDate: any;
  originalEndTime: Moment;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private utilityService: UtilityService) { }

  ngOnInit(): void {
    this.initiFormBuilder();
    this.recurrenceForm?.get('recurrence')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((recurrenceValue: any) => {
        if ((recurrenceValue === 'CUSTOM' && this.options.state === 'edit') || this.options.state === 'new') {
          this.setRepeatDay();
        }
        this.setDefaultEndValues();
        this.constructRrule();
      });

    this.recurrenceForm?.controls?.repeat?.get('type')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((value: any) => {
        this.setDefaultEndValues();
        const daysArray = [];
        const daysObject = this.days[0];

        if (value === 'MONTHS') {
          for (let i = 0; i < 5; i++) {
            daysArray.push(_.cloneDeep(daysObject));
          }
        } else {
          daysArray.push(daysObject);
        }
        this.days = daysArray;
        this.constructRrule();
      });

    this.recurrenceForm?.controls?.repeat?.get('count')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((value: any) => {
        if (value > 0) {
          this.constructRrule();
        }
      });

    this.recurrenceForm?.controls?.recurrence_ends?.get('type')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((value: any) => {
        if (value === 'recurrence_ends_on' && this.options?.state !== 'edit') {
          this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after').setValue(1);
          this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after').updateValueAndValidity();
        }
        this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on')?.clearValidators();
        this.constructRrule();
      });

    this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$), delay(500), distinctUntilChanged())
      ?.subscribe((value: any) => {
        if (value > 0) {
          this.constructRrule();
        }
      });

    this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((value: any) => {
        if (this.originalEndTime) {
          const originalEndTime: string = moment(moment(value).format('YYYY-MM-DD') + ' ' + this.originalEndTime.clone().format('HH')+':'+this.originalEndTime.clone().format('mm')+':00', 'YYYY-MM-DD HH:mm:ss').utc().toISOString();
          this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on').setValue(originalEndTime, { emitEvent: false });
        }
        this.constructRrule();
      });

    this.recurrenceForm?.get('recurrence')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((value: any) => {
        this.constructRrule();
      });

      this.recurrenceForm?.get('date_time')?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((value) => {
        if (this.options.state === 'new') {
          this.setDefaultEndValues();
        }
        else if (this.options.state === 'edit') {
          this.recurrenceForm.get('recurrence_ends.ends_after').setValue(this.totalInstance || 1, { emitEvent: false });
          this.constructRrule(true);
        }
      });

    this.recurrenceForm?.valueChanges
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe(() => {
        this.setupErrorHandling();
      });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('today' in changes) {
        this.minEndDate = this.today;
        const recurrenceEndsOnDate: Moment = moment(this.recurrenceForm.get('recurrence_ends').get('ends_on').value);
        if (moment(this.today).isAfter(recurrenceEndsOnDate)) {
          this.setDefaultEndValues();
        }
    }
  }

  initiFormBuilder(): void {
    const recurrence = this.recurrenceForm?.get('recurrence')?.value;
    const type = this.recurrenceForm?.controls?.repeat?.get('type')?.value;
    const count = this.recurrenceForm?.controls?.repeat?.get('count')?.value;
    if ((recurrence === 'CUSTOM' && this.options.state === 'edit') || this.options.state === 'new') {
      this.setRepeatDay();
    }
    this.setDefaultEndValues();
    const daysArray = [];
    const daysObject = this.days[0];

    if (type === 'MONTHS') {
      for (let i = 0; i < 5; i++) {
        daysArray.push(_.cloneDeep(daysObject));
      }
    } else {
      daysArray.push(daysObject);
    }
    this.days = daysArray;
    const rrule = this.recurrenceForm.get('RRule')?.value;
    const rruleString = rrule.toString().replace(':', '=').replace('\n', ';').replace('RRULE:', '');
    if (this.options.state !== 'new') {
      this.parseStringToRRule(rruleString);
    }
    else {
      this.minEndDate = this.today;
    }
    this.originalEndTime = moment.utc(this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on').value).clone();
    if (type === 'recurrence_ends_on' && this.options?.state !== 'edit') {
      this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after').setValue(1);
      this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after').updateValueAndValidity();
    }
    this.constructRrule();
    if (this.totalInstance) {
      this.recurrenceForm.get('recurrence_ends').get('ends_after').addValidators([Validators.min(this.totalInstance || 1)]);
    }
    else {
      this.recurrenceForm.get('recurrence_ends').get('ends_after').addValidators(Validators.max(1000));
    }
  }
  setDefaultEndValues(): void {
    if (this.options.state !== 'new' || this.recurrenceForm?.controls?.recurrence_ends?.get('type')?.dirty) {
      return;
    }
    const recurrenceValue: string = this.recurrenceForm?.controls?.recurrence?.value;
    const recurrenceStartDate: AbstractControl = this.recurrenceForm?.controls?.date_time;
    const recurrenceEndDate: AbstractControl = this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on');

    const date = moment.utc(recurrenceStartDate.value).set(
      {
        hour: moment.utc(recurrenceEndDate.value).get('hour'),
        minute: moment.utc(recurrenceEndDate.value).get('minute'),
        second: 0,
        millisecond: 0,
      });

    switch (recurrenceValue) {
      case 'DAILY':
        date.add(7, 'd');
        recurrenceEndDate.setValue(date.toISOString(), { emitEvent: false });
        break;
      case 'WEEKLY':
        date.add(1, 'month');
        recurrenceEndDate.setValue(date.toISOString(), { emitEvent: false });
        break;
      case 'MONTHLY':
      case 'MONTHLY-ORDINAL-WEEK':
        date.add(3, 'month');
        recurrenceEndDate.setValue(date.toISOString(), { emitEvent: false });
        break;
      case 'YEARLY':
        date.add(2, 'years');
        recurrenceEndDate.setValue(date.toISOString());
        break;
      case 'CUSTOM':
        const recurrenceRepeatType: AbstractControl = this.recurrenceForm?.controls?.repeat?.get('type');
        date.add(recurrenceRepeatType.value === 'WEEKS' ? 1 : 3, 'month');
        recurrenceEndDate.setValue(date.toISOString(), { emitEvent: false });
        break;
    }
  }
  getDate(date: Date): string {
    return moment(date).format('Do');
  }

  getDay(date: Date): string {
    return moment(date).format('dddd');
  }

  getMonthDay(date: Date): string {
    return moment(date).format('MMMM Do');
  }

  getDayWithWeekCount(date: Date): string {
    const targetDate = moment(date);
    const dayOfWeek = targetDate.format('ddd');
    const dayOfMonth = targetDate.date();
    const firstTargetDayOfMonth = moment(date).startOf('month').day(targetDate.day());

    const weekDifference = Math.floor((dayOfMonth - firstTargetDayOfMonth.date()) / 7);
    const ordinalArray = ['1st', '2nd', '3rd', '4th', '5th'];
    let ordinalIndex = Math.min(weekDifference, ordinalArray.length - 1);
    ordinalIndex = ordinalIndex < 1 ? 0 : ordinalIndex;
    return `${ordinalArray[ordinalIndex]} ${dayOfWeek}`;
  }

  getRecurrenceUntilString(): string {
    return this.rruleOptions?.toText() ?? '';
  }

  getRRleAsString(ruleString: any): string {
    if (ruleString) {
      const rule = RRule?.fromString(ruleString);
      return rule?.toText() ?? '';
    }
    return '';
  }

  setRepeatDay(): void {
    const date = this.recurrenceForm?.get('date_time').value || this.today;
    const selectedDay = moment(date).day()-1;
    this.days.map((item) => {
      if (this.options.state === 'new') {
        item.week.map((day: { label: string; value: boolean }) => {
          if (item.week[selectedDay < 0 ? 0 : selectedDay ].label === day.label) {
            day.value = true;
          } else {
            day.value = false;
          }
        });
      }
    });
  }
  parseStringToRRule(rRuleString: string): void {
    const options = RRule.parseString(rRuleString);
    this.recurrenceForm.get('recurrence_ends').get('ends_on').setValue(moment.utc(options.until).toISOString(), { emitEvent: false });
    this.recurrenceForm.get('recurrence_ends').get('ends_after').setValue(options.interval || 1, { emitEvent: false });
    if (this.options.state !== 'new') {
      this.recurrenceForm.get('recurrence').disable();
      this.minEndDate = moment.utc(options?.until).tz(this.timezone);
    }
    this.rruleOptions = new RRule(options);
    this.setDaysFromRRULE(this.rruleOptions.options);

  }

  setDaysFromRRULE(option: any): void {
    const options: any = { ...option };
    const frequency = RRule?.FREQUENCIES[options.freq];
    const dayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    let days = [];
    if (options.byweekday || options.bynweekday) {
      days = options.byweekday
        ? options.byweekday.map((day) => {
          if (day.n === undefined) {
            return `${dayNames[day?.weekday ? day?.weekday : day]}`;
          } else if (day.n) {
            return `${day.n === -1 ? 0 : day.n}${dayNames[day.weekday]}`;
          }
        })
        : options.bynweekday.map(day => `${day[1] === -1 ? 0 : day[1]}${dayNames[day[0]]}`);
      days.forEach((dayKey: any) => {
        const weekIndex = parseInt(isNaN(dayKey[0]) ? '1' : dayKey[0], 10) - 1;
        const dayCode = isNaN(dayKey[0]) ? dayKey : dayKey.substring(1);
        const day = this.days[weekIndex < 0 ? 4 : weekIndex]?.week?.find(d => d.key === dayCode);
        if (day) {
          day.value = true;
        }
      });
    }
    const currentDay = new Date().getDay();
    // Set recurrence type
    if (frequency === 'YEARLY' && days.length === 0) {
      this.recurrenceForm.get('recurrence').setValue('YEARLY');
    } else if (frequency === 'MONTHLY' && options?.bymonthday?.length > 0 && options?.interval === 1) {
      this.recurrenceForm.get('recurrence').setValue('MONTHLY');
    } else if (frequency === 'MONTHLY' && (options?.byweekday?.length === 1 || !this.options?.custom)) {
      this.recurrenceForm.get('recurrence').setValue('MONTHLY-ORDINAL-WEEK');
    } else if (frequency === 'WEEKLY' && (options?.byweekday?.length === 1 || !this.options?.custom) && options?.interval === 1) {
      const byWeekday = options.byweekday[0]; // Assuming byweekday is an array
      if (byWeekday === currentDay || !this.options?.custom) {
        this.recurrenceForm.get('recurrence').setValue('WEEKLY');
      }
      // this.recurrenceForm.get('recurrence').setValue('WEEKLY');
    } else if (frequency === 'DAILY') {
      this.recurrenceForm.get('recurrence').setValue('DAILY');
    }
  }

  setRecurrenceEnd(value: string): void {
    if (this.recurrenceForm?.controls?.recurrence_ends?.get('type')?.value !== value) {
      this.recurrenceForm?.controls?.recurrence_ends?.get('type')?.setValue(value);
    }
  }

  checkEndsAfterValue(): void {
    const endsAfterControl = this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after');
    if (endsAfterControl.value > 0) {
      this.constructRrule();
    }
  }

  constructRrule(preserveEndDate: boolean = false): void {
    if (this.recurrenceForm?.get('recurrence').value === '' || this.recurrenceFormErrorMessages['date_time']
      || this.recurrenceFormErrorMessages['ends_after']
      || this.recurrenceFormErrorMessages['count']
    ) {
      return;
    }
    const until = this.recurrenceForm?.controls?.recurrence_ends?.get('type').value === 'recurrence_ends_on' && !preserveEndDate ? 'date' : 'count';
    const start_date = this.recurrenceForm?.controls?.date_time.value;
    // eslint-disable-next-line max-len
    const end_value = this.recurrenceForm?.controls?.recurrence_ends?.get('type').value === 'recurrence_ends_on' && !preserveEndDate ? this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on').value :
      this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after').value;
    const recurrence_value = this.recurrenceForm?.get('recurrence').value;
    const customValue = {
      count: this.recurrenceForm?.controls?.repeat?.get('count').value,
      type: this.recurrenceForm?.controls?.repeat?.get('type').value,
      days: this.days
    };
    const rrulrOptions = this.generateRecurrenceRule(recurrence_value, start_date, end_value, until, customValue);
    this.rruleOptions = new RRule(rrulrOptions);
    const dates = [];
    this.rruleOptions?.all()?.forEach((dateString) => {
      const parsedDate = moment(dateString).utc().format('YYYY-MM-DD HH:mm:ss');
      dates.push(parsedDate);
    });

    this.rruleText = this.utilityService.getRruleText(this.rruleOptions.toString(), moment.tz.guess());
    this.totalInstance = this.rruleOptions.count();
    this.recurrenceForm?.controls?.RRule.setValue(this.rruleOptions);
    const endsAfterControl = this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after');
    const endsOnControl = this.recurrenceForm?.controls?.recurrence_ends?.get('ends_on');
    endsAfterControl?.setValue(Number(this.totalInstance),{emitEvent: false});
    endsAfterControl?.updateValueAndValidity({emitEvent: false});
    endsOnControl?.setValue(moment.utc(rrulrOptions.until).toISOString(),{emitEvent: false});
  }

  setRecurrenceRepeatDay(clicked_day: any, week_index: any): void {
    const selected_days = this.days[week_index].week.filter(item => item.value);
    clicked_day.value = clicked_day.value === true && selected_days.length === 1 &&
      this.recurrenceForm?.controls?.repeat?.get('type').value !== 'MONTHS'
      ? clicked_day.value : !clicked_day.value;
    this.constructRrule();
  }

  setupErrorHandling(): void {
    const controls = {
      'ends_after': this.recurrenceForm?.controls?.recurrence_ends?.get('ends_after'),
      'date_time': this.recurrenceForm?.controls?.date_time,
      'count': this.recurrenceForm?.controls?.repeat?.get('count'),
    };
    Object.keys(controls).forEach((controlName) => {
      const control = controls[controlName];
      if (control) {
        control.valueChanges.subscribe(() => {
          this.updateErrorMessage(controlName, control);
        });

        control.statusChanges.subscribe(() => {
          this.updateErrorMessage(controlName, control);
        });
      }
    });
  }

  updateErrorMessage(controlName: string, control: AbstractControl): void {
    this.recurrenceFormErrorMessages[controlName] = this.hasError(controlName, control);
  }

  hasError(controlName: string, control: AbstractControl): string {
    if (control?.touched || control?.dirty) {
      if (control?.errors?.max || control?.errors?.min) {
        if (controlName === 'count') {
          return 'route.count_hundred.error';
        }
        if (controlName === 'ends_after' && control?.errors?.min) {
          return 'non_job_event.ends_after.error';
        }
        return 'route.count_thousand.error';
      } else if (control?.errors?.pattern) {
        return 'field.pattern.number.error.message';
      } else if (control?.errors?.required) {
        return 'text.field_is_required';
      }
    }
    return '';
  }

  setDate(picker): void {
    const newDate = moment.tz(moment(this.recurrenceForm?.get('date_time').value).format('YYYY-MM-DD HH:mm:ss'), this.timezone);
    picker._selected = newDate;
  }


  generateRecurrenceRule(recurrenceValue: string, startDate: any, endValue: any, until: string, customValue?: any, timezone: string = this.utilityService.timeZone): any {
    const rruleOptions: any = {};
    const selectedDay = dayjs.utc(startDate).day() - 1;

    switch (recurrenceValue) {
      case 'DAILY':
        rruleOptions.freq = RRule.DAILY;
        break;
      case 'WEEKLY':
        rruleOptions.freq = RRule.WEEKLY;
        rruleOptions.byweekday = new Weekday(selectedDay < 0 ? 6 : selectedDay);
        break;
      case 'MONTHLY':
        rruleOptions.freq = RRule.MONTHLY;
        rruleOptions.bymonthday = dayjs.utc(startDate).date();
        break;
      case 'MONTHLY-ORDINAL-WEEK':
        const targetDate = dayjs(startDate);
        const startDateOfMonth = dayjs(targetDate).startOf('month');
        const weekCount = targetDate.diff(startDateOfMonth, 'week');
        rruleOptions.byweekday = new Weekday(selectedDay < 0 ? 6 : selectedDay, weekCount + 1);
        rruleOptions.freq = RRule.MONTHLY;
        break;
      case 'YEARLY':
        rruleOptions.freq = RRule.YEARLY;
        rruleOptions.bymonth = dayjs.utc(startDate).month() + 1;
        rruleOptions.bymonthday = dayjs.utc(startDate).date();
        break;
      case 'CUSTOM':
        rruleOptions.freq = customValue.type === 'WEEKS' ? RRule.WEEKLY : RRule.MONTHLY;
        const byWeekday = [];
        customValue.days.map((week, weekIndex) => {
          week.week.map((item, dayIndex) => {
            if (item.value && customValue.type === 'WEEKS') {
              byWeekday.push(new Weekday(dayIndex));
            }
            let weekValue = weekIndex + 1;
            weekValue = weekValue === 5 ? -1 : weekValue;
            if (item.value && customValue.type === 'MONTHS') {
              byWeekday.push(new Weekday(dayIndex, weekValue));
            }
          });
        });
        rruleOptions.byweekday = byWeekday;
        rruleOptions.interval = Number(customValue.count);
        break;
    }

    const recurrenceStart = dayjs(startDate);

    rruleOptions.dtstart = recurrenceStart.clone().toDate();

    if (until === 'date') {
      const recurrenceEnd = dayjs(dayjs(endValue).format('YYYY-MM-DD') + ' ' + recurrenceStart.clone().format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
      rruleOptions.until = dayjs(recurrenceEnd).toDate();
    } else {
      rruleOptions.count = endValue;
      const rruleDates = new RRule(rruleOptions)?.all();
      rruleOptions.until = dayjs(rruleDates[rruleDates?.length - 1]).toDate();
      delete rruleOptions.count;
    }
    return rruleOptions;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.originalEndTime = null;
  }

}
